import { type FieldErrors, type UseFieldArrayReturn, type UseFormRegister, type UseFormSetValue, type UseFormWatch } from "react-hook-form";
import { Panel } from "primereact/panel";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { FormField, RelativeDatePicker } from "@/components/forms";
import { formStyles } from "@/components/forms/formStyles";
import { getRhfErrorMessageAtPath } from "@/lib/rhf-errors";
import type { BookableEventFormData } from "@/lib/event-schemas";

function formatLabel(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() !== "" ? value : fallback;
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === "number" && !Number.isNaN(value) ? value : fallback;
}

function dateOrNull(value: unknown): Date | null {
  return value instanceof Date ? value : null;
}

export function PricePointFields(props: {
  fields: Array<{ id: string }>;
  errors: FieldErrors<BookableEventFormData>;
  watch: UseFormWatch<BookableEventFormData>;
  setValue: UseFormSetValue<BookableEventFormData>;
  register: UseFormRegister<BookableEventFormData>;
  append: UseFieldArrayReturn<BookableEventFormData, "pricePoints">["append"];
  remove: UseFieldArrayReturn<BookableEventFormData, "pricePoints">["remove"];
}) {
  const pricePointsRootError = getRhfErrorMessageAtPath(props.errors, "pricePoints");

  // Calculate reference dates for RelativeDatePicker
  const sessions = props.watch("sessions");
  const bookingClosesAt = props.watch("bookingClosesAt");

  const earliestSessionStart =
    sessions && sessions.length > 0
      ? sessions.reduce<Date | null>((earliest: Date | null, session: BookableEventFormData["sessions"][number]) => {
          const startsAt = session?.startsAt;
          if (!(startsAt instanceof Date)) return earliest;
          if (!earliest || startsAt < earliest) return startsAt;
          return earliest;
        }, null)
      : null;

  const referenceDateForFrom = earliestSessionStart || bookingClosesAt || null;
  const referenceDateLabelForFrom = earliestSessionStart
    ? "event start"
    : bookingClosesAt
      ? "booking close"
      : undefined;

  const referenceDateForUntil = bookingClosesAt || earliestSessionStart || null;
  const referenceDateLabelForUntil = bookingClosesAt
    ? "booking close"
    : earliestSessionStart
      ? "event start"
      : undefined;

  return (
    <Panel header="Price Points">
      <div className="flex flex-column gap-2">
        {pricePointsRootError ? (
          <small
            className="p-error"
            style={{
              color: "#dc3545",
              display: "block",
              marginBottom: "0.5rem",
            }}
          >
            {pricePointsRootError}
          </small>
        ) : null}

        {props.fields.map((field, index) => {
          const labelName = `pricePoints.${index}.label` as const;
          const priceCentsName = `pricePoints.${index}.priceCents` as const;
          const depositCentsName = `pricePoints.${index}.depositCents` as const;
          const descriptionName = `pricePoints.${index}.description` as const;
          const availableFromName = `pricePoints.${index}.availableFrom` as const;
          const availableUntilName = `pricePoints.${index}.availableUntil` as const;
          const balanceDueAtName = `pricePoints.${index}.balanceDueAt` as const;

          const labelError = getRhfErrorMessageAtPath(props.errors, labelName);
          const priceCentsError = getRhfErrorMessageAtPath(props.errors, priceCentsName);
          const depositCentsError = getRhfErrorMessageAtPath(props.errors, depositCentsName);
          const descriptionError = getRhfErrorMessageAtPath(props.errors, descriptionName);
          const balanceDueAtError = getRhfErrorMessageAtPath(props.errors, balanceDueAtName);

          const labelValue = props.watch(labelName);
          const depositCentsValue = props.watch(depositCentsName);

          return (
            <div
              key={field.id}
              className="flex flex-column gap-1"
              style={{
                padding: "1rem",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                backgroundColor: "#f8f9fa",
              }}
            >
              <div className="flex justify-content-between align-items-center mb-2">
                <label className="font-semibold" style={{ color: "#212529" }}>
                  {formatLabel(labelValue, `Price Point ${index + 1}`)}
                </label>
                <Button
                  type="button"
                  icon="pi pi-trash"
                  className="p-button-text p-button-danger p-button-sm"
                  onClick={() => props.remove(index)}
                  aria-label="Remove price point"
                />
              </div>

              <div className="grid" style={{ rowGap: "0.25rem" }}>
                <div className="col-12 md:col-6 field">
                  <FormField
                    id={labelName}
                    label="Label"
                    required
                    error={labelError}
                  >
                    <InputText
                      {...props.register(labelName)}
                      placeholder="e.g., Standard"
                      className={labelError ? "p-invalid w-full" : "w-full"}
                      style={formStyles.input}
                    />
                  </FormField>
                </div>

                <div className="col-12 md:col-6 field">
                  <FormField
                    id={priceCentsName}
                    label="Price"
                    required
                    error={priceCentsError}
                  >
                    <InputNumber
                      value={
                        numberOr(props.watch(priceCentsName), 0) / 100
                      }
                      onValueChange={(e) =>
                        props.setValue(
                          priceCentsName,
                          Math.round((e.value || 0) * 100),
                          { shouldValidate: true },
                        )
                      }
                      mode="decimal"
                      min={0}
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      prefix="£"
                      className={priceCentsError ? "p-invalid w-full" : "w-full"}
                      inputStyle={formStyles.input}
                    />
                  </FormField>
                </div>

                <div className="col-12 md:col-6 field">
                  <FormField
                    id={depositCentsName}
                    label="Deposit"
                    optional
                    error={depositCentsError}
                    helperText="Amount to charge on booking approval (must be at least £5 less than total price)"
                  >
                    <InputNumber
                      value={
                        typeof depositCentsValue === "number"
                          ? depositCentsValue / 100
                          : null
                      }
                      onValueChange={(e) => {
                        const value =
                          e.value !== null
                            ? Math.round((e.value || 0) * 100)
                            : null;
                        props.setValue(depositCentsName, value, {
                          shouldValidate: true,
                        });
                        if (!value) {
                          props.setValue(balanceDueAtName, null);
                        }
                      }}
                      mode="decimal"
                      min={0}
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      prefix="£"
                      placeholder="No deposit"
                      className={depositCentsError ? "p-invalid w-full" : "w-full"}
                      inputStyle={formStyles.input}
                    />
                  </FormField>
                </div>

                <div className="col-12 field">
                  <FormField
                    id={descriptionName}
                    label="Description"
                    optional
                    error={descriptionError}
                  >
                    <InputTextarea
                      {...props.register(descriptionName)}
                      rows={2}
                      placeholder="e.g., Standard pricing for all participants"
                      className="w-full"
                      style={formStyles.input}
                    />
                  </FormField>
                </div>

                <div className="col-12 field">
                  <RelativeDatePicker
                    id={availableFromName}
                    label="Available From"
                    optional
                    value={dateOrNull(props.watch(availableFromName))}
                    onChange={(date) =>
                      props.setValue(availableFromName, date, {
                        shouldValidate: true,
                      })
                    }
                    referenceDate={referenceDateForFrom}
                    referenceDateLabel={referenceDateLabelForFrom}
                  />
                </div>

                <div className="col-12 field">
                  <RelativeDatePicker
                    id={availableUntilName}
                    label="Available Until"
                    optional
                    value={dateOrNull(props.watch(availableUntilName))}
                    onChange={(date) =>
                      props.setValue(availableUntilName, date, {
                        shouldValidate: true,
                      })
                    }
                    referenceDate={referenceDateForUntil}
                    referenceDateLabel={referenceDateLabelForUntil}
                  />
                </div>

                {depositCentsValue != null ? (
                  <div className="col-12 field">
                    <RelativeDatePicker
                      id={balanceDueAtName}
                      label="Balance Due At"
                      optional
                      error={balanceDueAtError}
                      value={dateOrNull(props.watch(balanceDueAtName))}
                      onChange={(date) =>
                        props.setValue(balanceDueAtName, date, {
                          shouldValidate: true,
                        })
                      }
                      referenceDate={earliestSessionStart}
                      referenceDateLabel="event start"
                      helperText="When the remaining balance should be collected"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}

        <Button
          type="button"
          label="Add Price Point"
          icon="pi pi-plus"
          className="p-button-outlined"
          onClick={() =>
            props.append({
              label: "",
              description: "",
              priceCents: 0,
              currency: "gbp",
              sortOrder: props.fields.length,
              availableFrom: null,
              availableUntil: null,
              depositCents: null,
              balanceDueAt: null,
              id: undefined,
            })
          }
        />
      </div>
    </Panel>
  );
}


